import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HeartLogo } from '../components/hbs/HeartLogo';
import { recoveryDeny } from '../api/client';

// Standalone, no-login landing for the "someone requested recovery of your
// account" email's one-click Deny link (`/safe/recovery/deny?token=…`). The
// signed token in the URL is the authorization — a locked-out owner can cancel
// an in-flight reset without signing in. The endpoint is idempotent and uniform
// (a valid, expired, or already-resolved token all return {denied:true}); only
// a malformed/forged token errors.
type Phase = 'processing' | 'denied' | 'invalid';

export default function RecoveryDenyPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [phase, setPhase] = useState<Phase>(token ? 'processing' : 'invalid');
  const ranRef = useRef(false);

  useEffect(() => {
    if (!token || ranRef.current) return;
    ranRef.current = true;
    void (async () => {
      const r = await recoveryDeny({ token });
      setPhase(r.ok ? 'denied' : 'invalid');
    })();
  }, [token]);

  return (
    <main className="min-h-screen bg-hbs-bg flex items-center justify-center p-6">
      <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border p-8 shadow-hbs-lg max-w-sm w-full">
        <div className="flex flex-col items-center gap-3 mb-6">
          <HeartLogo size={56} />
          <span className="font-display font-semibold text-[18px] tracking-hbs-tight text-hbs-text">
            Hey Be Safe
          </span>
        </div>
        {phase === 'processing' && (
          <>
            <h1 className="text-2xl font-display font-semibold text-hbs-text mb-2">Cancelling recovery…</h1>
            <p className="text-sm text-hbs-text-mute">One moment.</p>
          </>
        )}
        {phase === 'denied' && (
          <>
            <h1 className="text-2xl font-display font-semibold text-hbs-text mb-2">Recovery request denied</h1>
            <p className="text-sm text-hbs-text-mute">
              The password-reset request for your account has been cancelled. If you didn't
              recognize it, your safe password is unchanged and your safe is secure — consider
              signing in to review your recovery contacts.
            </p>
          </>
        )}
        {phase === 'invalid' && (
          <>
            <h1 className="text-2xl font-display font-semibold text-hbs-text mb-2">Link expired or invalid</h1>
            <p className="text-sm text-hbs-text-mute">
              This deny link is missing, malformed, or has expired. If a recovery request is
              still pending and you didn't start it, sign in to review it.
            </p>
          </>
        )}
        <div className="mt-6 text-sm text-hbs-text-mute">
          <Link to="/auth" className="hover:text-hbs-text">← Back to sign in</Link>
        </div>
      </div>
    </main>
  );
}
